import { Button } from '@fountain/ripple/button';
import { Tag } from '@fountain/ripple/tag';
import { Text } from '@fountain/ripple/text';
import { TextField } from '@fountain/ripple/text-field';
import React, { useId } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import { reportTestMessages as m } from './ReportTestScreen.messages';
import type { ReportTestInput, useReportTest } from './useReportTest';

interface DisplayActions {
  fieldErrors: Record<string, 'required' | 'invalid'>;
  error?: 'permission' | 'unavailable';
  onCancel: () => void;
  onInputChange: (key: string, value: string) => void;
}

export type ReportV1TestScreenProps = DisplayActions &
  Pick<ReturnType<typeof useReportTest>, 'status' | 'sources' | 'result'> & {
    mode: 'report-v1';
    inputs: {
      key: keyof ReportTestInput['bindings'];
      label: string;
      value: string;
      type: 'text' | 'email' | 'tel';
    }[];
    sourceNames: Record<string, string>;
    onRun: (bindings: ReportTestInput['bindings']) => void;
  };

export interface MatchingV2TestScreenProps extends DisplayActions {
  mode: 'matching-v2';
  inputs: {
    key: string;
    label: string;
    value: string;
    type: 'text' | 'email' | 'tel' | 'secure';
  }[];
  status: ReportV1TestScreenProps['status'] | 'starting';
  sources: ({ key: string; name: string } & (
    | { status: 'complete'; count: number; columns: string[] }
    | { status: 'pending' | 'failed'; count: null }
  ))[];
  result?: {
    verdict:
      | 'eligible'
      | 'not_eligible'
      | 'no_match'
      | 'ambiguous'
      | 'unavailable'
      | 'field_not_mapped';
    identityConfirmed: boolean;
    currentlyEmployed: boolean;
    foundBy?: 'phone' | 'email' | 'ssn';
    reason?:
      | 'first_name_mismatch'
      | 'different_people'
      | 'no_match_details'
      | 'exact_names'
      | 'all_hits_dropped'
      | 'blank_eligibility'
      | 'unexpected_eligibility'
      | 'conflicting_eligibility';
    employeeNumber?: string;
    eligibility?: string;
  };
  onRun: (bindings: Record<string, string>) => void;
}

export type ReportTestScreenProps =
  | ReportV1TestScreenProps
  | MatchingV2TestScreenProps;

const Section = styled.section`
  min-width: 0;
  color: var(--ds-color-neutral-900);
  overflow-wrap: anywhere;
`;
const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
  gap: 12px 16px;
  margin-top: 12px;
`;
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;
const Result = styled.div`
  border: 1px solid var(--ds-color-neutral-200);
  border-radius: var(--ds-radius-lg);
  padding: 16px;
  margin-top: 16px;
`;
const Facts = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 160px) minmax(0, 1fr);
  gap: 4px 12px;
  margin: 12px 0 0;
  dd {
    margin: 0;
    min-width: 0;
  }
`;

// The host owns selection, authority, lifecycle and safe projection. This leaf never evaluates a person.
export function ReportTestScreen(props: ReportTestScreenProps) {
  const {
    mode,
    inputs,
    status,
    fieldErrors,
    error,
    onRun,
    onCancel,
    onInputChange,
  } = props;
  const intl = useIntl();
  const id = useId();
  const busy = status === 'starting' || status === 'running';
  const modern = mode === 'matching-v2';
  const result = props.mode === 'matching-v2' ? props.result : undefined;
  const sources =
    props.mode === 'report-v1'
      ? props.sources.map((source, index) => ({
          key: source.reportKey,
          name:
            props.sourceNames[source.reportKey] ??
            intl.formatMessage(m.report, { number: index + 1 }),
          status:
            source.status === 'complete'
              ? ('complete' as const)
              : ('failed' as const),
          count: source.rowCount,
          columns: source.columnNames,
        }))
      : props.sources;
  const denied = status === 'refused' && error === 'permission';
  const readable = status === 'complete';
  const settled = sources.every(
    source => source.status === 'complete' && source.count !== null,
  );
  const finished = sources.every(source => source.status !== 'pending');
  const confirmed =
    readable &&
    settled &&
    result?.identityConfirmed &&
    (result.verdict === 'eligible' || result.verdict === 'not_eligible');
  const labels = {
    eligible: messages.verdictEligible,
    not_eligible: messages.verdictNotEligible,
    no_match: modern ? m.no_match : messages.verdictNoMatch,
    ambiguous: modern ? m.ambiguous : messages.verdictAmbiguous,
    unavailable: modern ? m.unavailable : messages.verdictUnavailable,
    field_not_mapped: messages.verdictFieldNotMapped,
  };
  const serverVerdict = props.result?.verdict;
  const current = confirmed && result.currentlyEmployed;
  const verdictLabel = !settled
    ? labels.unavailable
    : modern && current
      ? m.current
      : !confirmed &&
          (result?.verdict === 'eligible' || result?.verdict === 'not_eligible')
        ? labels.ambiguous
        : serverVerdict
          ? labels[serverVerdict]
          : null;
  const color =
    current || verdictLabel === labels.ambiguous
      ? 'amber'
      : confirmed
        ? result.verdict === 'eligible'
          ? 'green'
          : 'rose'
        : 'neutral';
  // Only known safe explanations render; person facts retain their confirmation guard.
  const reasonMessage =
    result?.reason && Object.prototype.hasOwnProperty.call(m, result.reason)
      ? m[result.reason as keyof typeof m]
      : undefined;
  const progress = sources.length
    ? intl.formatMessage(m.progress, {
        current: Math.min(
          sources.filter(source => source.status !== 'pending').length + 1,
          sources.length,
        ),
        total: sources.length,
      })
    : intl.formatMessage(m.searching);
  const notice =
    status === 'stale'
      ? m.stale
      : status === 'refused'
        ? denied
          ? m.refused
          : messages.dryRunErrorMessage
        : status === 'expired'
          ? m.expired
          : null;
  const explanations = [
    readable && settled && reasonMessage,
    confirmed &&
      !current &&
      result.foundBy &&
      (result.foundBy === 'phone'
        ? m.foundByPhone
        : result.foundBy === 'ssn'
          ? messages.foundBySsn
          : messages.foundByEmail),
    current && m.already_employed,
    readable &&
      settled &&
      props.mode === 'report-v1' &&
      props.result?.currentEmployment === true &&
      (serverVerdict === 'eligible' || serverVerdict === 'not_eligible') &&
      m.current,
    readable && !serverVerdict && settled && m.discovery,
  ];
  const facts: [string, string, string][] = sources.map(source => [
    `source:${source.key}`,
    source.name,
    [
      intl.formatMessage(
        source.status === 'pending'
          ? m.searching
          : source.status === 'failed' || source.count === null
            ? m.failed
            : source.count === 0
              ? m.empty
              : m.count,
        { count: source.count },
      ),
      source.status === 'complete' && source.columns.length > 0
        ? intl.formatMessage(m.columns, { columns: source.columns.join(', ') })
        : '',
    ]
      .filter(Boolean)
      .join(' · '),
  ]);
  if (confirmed && result.employeeNumber)
    facts.push([
      'employee',
      intl.formatMessage(messages.dryRunResultEmployeeNumberLabel),
      result.employeeNumber,
    ]);
  if (confirmed && !current && result.eligibility)
    facts.push([
      'eligibility',
      intl.formatMessage(messages.eligibilityFieldLabel),
      result.eligibility,
    ]);

  return (
    <Section aria-labelledby={`${id}-title`}>
      <Text variant="h4" id={`${id}-title`}>
        {intl.formatMessage(messages.dryRunCardTitle)}
      </Text>
      <Fields>
        {inputs.map(input => (
          <TextField
            key={input.key}
            label={input.label}
            value={input.value}
            type={input.type === 'secure' ? 'password' : input.type}
            autoComplete="off"
            error={
              fieldErrors[input.key]
                ? intl.formatMessage(m[fieldErrors[input.key]])
                : undefined
            }
            onChange={event => onInputChange(input.key, event.target.value)}
          />
        ))}
      </Fields>
      <Row>
        <Button
          size="sm"
          variant="secondary-brand"
          loading={busy}
          aria-describedby={busy ? `${id}-progress` : undefined}
          onClick={() => {
            if (!busy && !denied)
              onRun(
                Object.fromEntries(
                  inputs.map(input => [input.key, input.value]),
                ),
              );
          }}
          disabledReason={denied ? intl.formatMessage(m.refused) : undefined}
        >
          {intl.formatMessage(messages.dryRunButton)}
        </Button>
        {busy && (
          <>
            <Text id={`${id}-progress`} role="status">
              {progress}
            </Text>
            <Button size="sm" variant="text-neutral" onClick={onCancel}>
              {intl.formatMessage(globalMessages.cancel)}
            </Button>
          </>
        )}
      </Row>
      <div role={status === 'refused' ? 'alert' : 'status'} aria-live="polite">
        {notice && <Text>{intl.formatMessage(notice)}</Text>}
        {(readable || (busy && sources.length > 0)) && (
          <Result>
            {readable && serverVerdict && finished && (
              <Tag overflow="wrap" color={color}>
                {intl.formatMessage(verdictLabel ?? labels.unavailable)}
              </Tag>
            )}
            {explanations.map(
              message =>
                message && (
                  <Text key={message.id}>{intl.formatMessage(message)}</Text>
                ),
            )}
            <Facts>
              {facts.map(([key, label, value]) => (
                <React.Fragment key={key}>
                  <Text variant="body2" render={<dt />}>
                    {label}
                  </Text>
                  <Text render={<dd />}>{value}</Text>
                </React.Fragment>
              ))}
            </Facts>
          </Result>
        )}
      </div>
    </Section>
  );
}
